import React from "react";
import { Button } from "./ui/button";
import { RotateCw, Copy, Trash2 } from "lucide-react";

const numInput =
  "h-8 px-2 border border-[#A8C97D] rounded-none text-sm font-mono-data focus:border-[#5E8A1E] focus:outline-none";
const lbl = "text-[10px] uppercase tracking-wide text-zinc-500";

export default function PropertyPanel({ element, categories, onChange, onCommit, onRotate, onDuplicate, onDelete }) {
  if (!element) return null;
  const set = (patch) => onChange({ ...element, ...patch });

  return (
    <div className="space-y-3" data-testid="element-property-panel">
      <label className="flex flex-col gap-0.5">
        <span className={lbl}>Nume</span>
        <input
          data-testid="element-name-input"
          value={element.name}
          onChange={(e) => set({ name: e.target.value })}
          onBlur={onCommit}
          className="h-8 px-2 border border-[#A8C97D] rounded-none text-sm focus:border-[#5E8A1E] focus:outline-none"
        />
      </label>

      <label className="flex flex-col gap-0.5">
        <span className={lbl}>Categorie</span>
        <select
          data-testid="element-category-select"
          value={element.category_id}
          onChange={(e) => {
            const cat = categories.find((c) => c.id === e.target.value);
            set({
              category_id: e.target.value,
              length: cat?.length ?? element.length,
              width: cat?.width ?? element.width,
            });
            setTimeout(onCommit, 0);
          }}
          className="h-8 px-2 border border-[#A8C97D] rounded-none text-sm bg-white focus:border-[#5E8A1E] focus:outline-none"
        >
          {categories.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </label>

      <div className="grid grid-cols-2 gap-2">
        <label className="flex flex-col gap-0.5">
          <span className={lbl}>Lungime (m)</span>
          <input data-testid="element-length-input" type="number" step="0.1" value={element.length}
            onChange={(e) => set({ length: parseFloat(e.target.value) || 0 })} onBlur={onCommit} className={numInput} />
        </label>
        <label className="flex flex-col gap-0.5">
          <span className={lbl}>Lățime (m)</span>
          <input data-testid="element-width-input" type="number" step="0.1" value={element.width}
            onChange={(e) => set({ width: parseFloat(e.target.value) || 0 })} onBlur={onCommit} className={numInput} />
        </label>
      </div>

      <label className="flex flex-col gap-0.5">
        <span className={lbl}>Consum electric (kW)</span>
        <input data-testid="element-consumption-input" type="number" step="0.1" value={element.consumption}
          onChange={(e) => set({ consumption: parseFloat(e.target.value) || 0 })} onBlur={onCommit} className={numInput} />
      </label>

      <label className="flex flex-col gap-0.5">
        <span className={lbl}>Consum noaptea (kW)</span>
        <input data-testid="element-consumption-night-input" type="number" step="0.1" value={element.consumption_night ?? 0}
          onChange={(e) => set({ consumption_night: parseFloat(e.target.value) || 0 })} onBlur={onCommit} className={numInput} />
      </label>

      <label className="flex flex-col gap-0.5">
        <span className={lbl}>Rotație: {element.rotation}°</span>
        <input
          data-testid="element-rotation-slider"
          type="range" min="0" max="359" step="1"
          value={element.rotation}
          onChange={(e) => set({ rotation: parseInt(e.target.value) })}
          onMouseUp={onCommit}
          onTouchEnd={onCommit}
          className="accent-[#5E8A1E]"
        />
      </label>

      <label className="flex flex-col gap-0.5">
        <span className={lbl}>Mobilier</span>
        <textarea data-testid="element-mobilier-input" rows={2} value={element.mobilier || ""}
          onChange={(e) => set({ mobilier: e.target.value })} onBlur={onCommit}
          className="px-2 py-1 border border-[#A8C97D] rounded-none text-sm focus:border-[#5E8A1E] focus:outline-none resize-none" />
      </label>
      <label className="flex flex-col gap-0.5">
        <span className={lbl}>Branding</span>
        <textarea data-testid="element-branding-input" rows={2} value={element.branding || ""}
          onChange={(e) => set({ branding: e.target.value })} onBlur={onCommit}
          className="px-2 py-1 border border-[#A8C97D] rounded-none text-sm focus:border-[#5E8A1E] focus:outline-none resize-none" />
      </label>
      <label className="flex flex-col gap-0.5">
        <span className={lbl}>Altele</span>
        <textarea data-testid="element-altele-input" rows={2} value={element.altele || ""}
          onChange={(e) => set({ altele: e.target.value })} onBlur={onCommit}
          className="px-2 py-1 border border-[#A8C97D] rounded-none text-sm focus:border-[#5E8A1E] focus:outline-none resize-none" />
      </label>

      <div className="grid grid-cols-3 gap-2 pt-1">
        <Button data-testid="toolbar-rotate-element" onClick={onRotate}
          className="h-9 rounded-none bg-zinc-100 text-zinc-800 hover:bg-[#5E8A1E] hover:text-white border border-[#A8C97D]" title="Rotește 15°">
          <RotateCw size={15} strokeWidth={1.5} />
        </Button>
        <Button data-testid="toolbar-duplicate-element" onClick={onDuplicate}
          className="h-9 rounded-none bg-zinc-100 text-zinc-800 hover:bg-[#5E8A1E] hover:text-white border border-[#A8C97D]" title="Copiază">
          <Copy size={15} strokeWidth={1.5} />
        </Button>
        <Button data-testid="toolbar-delete-element" onClick={onDelete}
          className="h-9 rounded-none bg-zinc-100 text-zinc-800 hover:bg-[#E63946] hover:text-white border border-[#A8C97D]" title="Șterge">
          <Trash2 size={15} strokeWidth={1.5} />
        </Button>
      </div>
    </div>
  );
}
